import axios, { AxiosError } from 'axios';
import { Environment } from '../../../environment';
import { v4 as uuidv4 } from 'uuid';
import { Api } from '../axiosApi';

export interface IListagem {
  idlan: string;
  entrada: string;
  saida: string;
  datalan: Date;
  numero: string;
  valor: number;
  historico: string;
  // Só vem preenchido nos tenants com suporte a estorno (bancoteste/arjbjl) —
  // desfazer não apaga mais o lançamento, só marca (ver backend
  // Lanentrecontas/DeleteById.ts).
  revertido?: boolean;
  revertidoem?: Date | null;
}
export interface Ilancamento {
  identrada: string;
  idsaida: string;
  datalan: Date;
  numero: string;
  valor: number;
  historico: string;
}
type TotalCount = {
    data: IListagem[];
    totalCount: number;
}
interface ErrorResponse {
  errors: {
    default: string;
  };
}
const create = async (dados: Ilancamento): Promise<string | Error> => {
  try {
    const guuid = uuidv4();
    const { data} = await Api.post<IListagem>('/lanentrecontas',  {idlan: guuid, ...dados});
    if (Object.keys(data).length === 0) {
      return guuid;
    }
    return new Error('Erro ao criar o registro.');
  }  catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const getAll = async (page = 1, datainicial = '', datafinal = '', limit = Environment.LIMITE_DE_LINHAS): Promise<TotalCount | Error> => {
  try {
    const urlRelativa = `/lanentrecontas?page=${page}&limit=${limit}&datainicial=${datainicial}&datafinal=${datafinal}`;
    const { data, headers } = await Api.get(urlRelativa);
    if (data) {
      return {
        data,
        totalCount: Number(headers['x-total-count'] || limit),
      };
    }
    return new Error('Erro ao listar os registros.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const deleteById = async (id: string): Promise<void | Error> => {
  try {
    await Api.delete(`/lanentrecontas/${id}`);
  }catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const getById = async (id: string): Promise<Ilancamento | Error> => {
  try {
    const { data } = await Api.get(`/lanentrecontas/${id}`);
    if (data) {
      return data;
    }
    return new Error('Erro ao consultar o registro.');
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ErrorResponse>;
      if (axiosError.response) {
        const errorMessage = axiosError.response.data.errors?.default || 'Erro desconhecido';
        return new Error(errorMessage);
      } else if (axiosError.request) {
        return new Error('Nenhuma resposta do servidor');
      } else {
        return new Error(axiosError.message);
      }
    } else {
      return new Error('Erro inesperado');
    }
  }
};
const mensagemDeErro = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response) return new Error(axiosError.response.data.errors?.default || 'Erro desconhecido');
    if (axiosError.request) return new Error('Nenhuma resposta do servidor');
    return new Error(axiosError.message);
  }
  return new Error('Erro inesperado');
};

// --- Caixa → Cofre no dia do caixa (Conciliação) -----------------------------
// O lançamento cai no dia do caixa (`data`, AAAA-MM-DD), mesmo que seja um dia
// anterior; o histórico registra quando a transferência foi feita. Sem
// `valor`, o backend transfere todo o saldo do caixa até o fim daquele dia.
export interface ITransferenciaCaixaCofre {
  idcaixa: string;
  idcofre: string;
  data: string;
  valor?: number;
  observacao?: string;
}

// Saldo do caixa no fim do dia `data` (tudo até aquele dia).
const saldoCaixaNaData = async (idcaixa: string, data: string): Promise<number | Error> => {
  try {
    const { data: resposta } = await Api.get<{ saldo: number }>('/lanentrecontas/caixacofre/saldo', { params: { idcaixa, data } });
    return Number(resposta.saldo || 0);
  } catch (error) {
    return mensagemDeErro(error);
  }
};

const transferirCaixaParaCofre = async (dados: ITransferenciaCaixaCofre): Promise<{ idlan: string; valor: number } | Error> => {
  try {
    const { data } = await Api.post<{ idlan: string; valor: number }>('/lanentrecontas/caixacofre', dados);
    return data;
  } catch (error) {
    return mensagemDeErro(error);
  }
};

export const LancamentoEntreConstasService = {
  getAll,
  create,
  deleteById,
  getById,
  saldoCaixaNaData,
  transferirCaixaParaCofre,
}; 